import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getSocket } from 'utils/socketManager';
import { getToken } from 'utils/crypto';
import { chatActions } from '../../store';

/**
 * Registers global socket listeners for chat events.
 * Call ONCE at the top level (ChatDrawer) — not in child components.
 */
export const useChatSocketListeners = () => {
    const dispatch = useDispatch();
    const handlersRef = useRef({});

    useEffect(() => {
        const authToken = getToken();
        if (!authToken) return;
        const socket = getSocket(authToken);
        if (!socket) return;

        // Store named handler refs so we only remove our own handlers on cleanup
        // Note: chat:unread-update, chat:new-chat, and chat:chat-deleted are
        // registered globally in UserInfo for badge updates (even when drawer is closed)
        const localUser = require('utils/crypto').getUserInfo() || {};
        const currentUserId = localUser?.id;

        handlersRef.current = {
            onNewMessage: (data) => dispatch(chatActions.chatReceiveMessage(data)),
            onMessageEdited: (data) => dispatch(chatActions.chatMessageEdited(data)),
            onMessageDeleted: (data) => dispatch(chatActions.chatMessageDeleted(data)),
            onChatRead: (data) => dispatch(chatActions.chatMessageRead({ ...data, currentUserId })),
            onMessageRead: (data) => dispatch(chatActions.chatMessageRead({ ...data, currentUserId })),
            onUserTyping: (data) => dispatch(chatActions.chatSetTyping(data)),
            onUserStoppedTyping: (data) => dispatch(chatActions.chatClearTyping(data)),
        };

        socket.on('chat:new-message', handlersRef.current.onNewMessage);
        socket.on('chat:message-edited', handlersRef.current.onMessageEdited);
        socket.on('chat:message-deleted', handlersRef.current.onMessageDeleted);
        socket.on('chat:chat-read', handlersRef.current.onChatRead);
        socket.on('chat:message-read', handlersRef.current.onMessageRead);
        socket.on('chat:user-typing', handlersRef.current.onUserTyping);
        socket.on('chat:user-stopped-typing', handlersRef.current.onUserStoppedTyping);

        return () => {
            socket.off('chat:new-message', handlersRef.current.onNewMessage);
            socket.off('chat:message-edited', handlersRef.current.onMessageEdited);
            socket.off('chat:message-deleted', handlersRef.current.onMessageDeleted);
            socket.off('chat:chat-read', handlersRef.current.onChatRead);
            socket.off('chat:message-read', handlersRef.current.onMessageRead);
            socket.off('chat:user-typing', handlersRef.current.onUserTyping);
            socket.off('chat:user-stopped-typing', handlersRef.current.onUserStoppedTyping);
        };
    }, []);
};

/**
 * Returns socket emitter functions for chat rooms.
 * Safe to call from any component — no listeners registered.
 */
export const useChatSocket = () => {
    const socketRef = useRef(null);

    useEffect(() => {
        const authToken = getToken();
        if (!authToken) return;
        socketRef.current = getSocket(authToken);
    }, []);

    const joinChat = useCallback((chatId) => {
        socketRef.current?.emit('chat:join', { chatId });
    }, []);

    const leaveChat = useCallback((chatId) => {
        socketRef.current?.emit('chat:leave', { chatId });
    }, []);

    const startTyping = useCallback((chatId) => {
        socketRef.current?.emit('chat:typing', { chatId });
    }, []);

    const stopTyping = useCallback((chatId) => {
        socketRef.current?.emit('chat:stop-typing', { chatId });
    }, []);

    return {
        joinChat,
        leaveChat,
        startTyping,
        stopTyping,
    };
};
